import { crmApiUrl } from "./api"
import { tokenStore } from "../../lib/tokenStore.js"

/**
 * Marketing ▸ Webinars → email-campaign hand-off.
 *
 * Step 1 of the webinar flow does not build an email of its own: it opens
 * THIS editor with crm-api's `webinar` layout selected and the event's details
 * already in the builder's sections, because the webinar email already has two
 * renderers (this builder and crm-api's server-side fallback) and a third
 * would drift from both.
 *
 * The webinar therefore has to learn which campaign became its invitation.
 * The editor auto-saves a draft, gets an id, and calls this — a DASHBOARD
 * endpoint (`/api/webinars/:id/link-campaign`), not a crm-api one, so it goes
 * through plain `fetch` with the dashboard session token, exactly like
 * `ai-audience-api.ts` does for the AI recipient bridge.
 */

/** The seed the Webinars page passes through `navigate(..., { state })`.
 * Structurally identical to `WebinarCampaignSeed` in packages/shared; declared
 * here so the walled CRM sub-app does not have to import across the boundary
 * for a shape it only reads. */
export interface WebinarCampaignSeedState {
  webinar_id: number
  name: string
  subject: string
  layout: "webinar"
  sections: unknown[]
}

/** Narrow an untrusted `location.state` into a usable seed. Returns null for
 * anything that is not a complete seed, so a stale or hand-crafted navigation
 * state can never half-populate the form. */
export function readWebinarSeed(state: unknown): WebinarCampaignSeedState | null {
  const seed = (state as { webinarSeed?: Partial<WebinarCampaignSeedState> } | null)?.webinarSeed
  if (!seed || typeof seed !== "object") return null
  if (typeof seed.webinar_id !== "number" || !Number.isFinite(seed.webinar_id)) return null
  if (!Array.isArray(seed.sections) || seed.sections.length === 0) return null
  if (typeof seed.subject !== "string" || !seed.subject.trim()) return null
  if (seed.layout !== "webinar") return null
  return {
    webinar_id: seed.webinar_id,
    name: typeof seed.name === "string" ? seed.name : seed.subject,
    subject: seed.subject,
    layout: "webinar",
    sections: seed.sections,
  }
}

/**
 * Tell a webinar which campaign is its invitation.
 *
 * Fail-soft on purpose: this is bookkeeping, not the operator's work. If it
 * fails, the campaign still exists and still sends — the webinar's step 1 just
 * does not show it as linked, and the operator can press the button again.
 */
export async function linkCampaignToWebinar(
  webinarId: number,
  campaignId: number,
  slot: "invite" | "thankyou" | "survey" = "invite",
): Promise<boolean> {
  const access = tokenStore.getAccess()
  try {
    const res = await fetch(crmApiUrl(`/api/webinars/${webinarId}/link-campaign`), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({ slot, campaign_id: campaignId }),
    })
    return res.ok
  } catch {
    return false
  }
}
